import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { toast } from "@mindstonehq/ui";
import { useEffect } from "react";

import {
  getApiUnreachableReason,
  isApiReachable,
  subscribeApiReachability,
} from "~/graphql/api-connectivity";

export const API_CONNECTIVITY_TOAST_ID = "api-unreachable";

/**
 * One calm, persistent notice while the browser cannot reach the Mindstone
 * API; gone on a successful request or recovery probe. Rides the app's existing Sonner
 * toaster (an accessible live region) rather than a new banner: the app and
 * reading shells are exactly viewport-height, so an in-flow bar would push
 * their bottom action bars below the fold. Mounted once, beside the root
 * <Outlet />, so every route gets it. Renders nothing itself.
 */
export function ApiConnectivityNotice() {
  const { t } = useLingui();
  useEffect(() => {
    // Our side (a gateway 502/503/504) must not blame the learner's connection.
    const gatewayMessage = t(msg`Mindstone is having trouble right now. We're on it.`);
    const networkMessage = t(
      msg`Mindstone can't be reached right now. Check your connection.`,
    );
    const sync = () => {
      if (isApiReachable()) {
        toast.dismiss(API_CONNECTIVITY_TOAST_ID);
        return;
      }
      const message =
        getApiUnreachableReason() === "gateway" ? gatewayMessage : networkMessage;
      toast.warning(message, {
        id: API_CONNECTIVITY_TOAST_ID,
        duration: Infinity,
        // Allow dismissal while the automatic recovery checks continue.
        closeButton: true,
      });
    };
    sync();
    return subscribeApiReachability(sync);
  }, [t]);
  return null;
}
